import { inject, Injectable } from '@angular/core';
import { ApolloError } from '@apollo/client';
import { Observable, take } from 'rxjs';
import { map, tap } from 'rxjs/operators';

import { UserService } from '@ppg/auth';
import { CartStateService } from '@ppg/core/services';

import { AddLineItemToCartResponse } from '../models/add-line-item-to-cart.response';
import { AddToCartVariables } from '../models/add-to-cart.variables';
import { CartJob } from '../models/cart-job.model';
import { AddToCartMutation } from '../mutations/add-to-cart.mutation';
import { AddToMyCartMutation } from '../mutations/add-to-my-cart.mutation';
import { DeleteCartGQL, DeleteCartResponse } from '../mutations/delete-cart.mutation';
import { GetCartJobGQL } from '../queries/cart-job.query';
import { GetCartQuery } from '../queries/get-cart.query';

@Injectable({
  providedIn: 'root',
})
export class CartService {
  readonly #addToCartMutation = inject(AddToCartMutation);
  readonly #addToMyCartMutation = inject(AddToMyCartMutation);
  readonly #getCartJob = inject(GetCartJobGQL);
  readonly #getCartQuery = inject(GetCartQuery);
  readonly #deleteCart = inject(DeleteCartGQL);
  readonly #userService = inject(UserService);
  readonly #cartStateService = inject(CartStateService);

  initCart() {
    const isB2BUser = this.#userService.isB2BUser();
    const cartQuery: Observable<{ totalItemsQuantity: number } | undefined> = isB2BUser
      ? this.getCartJob()
      : this.getCart();

    cartQuery
      .pipe(
        take(1),
        tap((cart) => this.#cartStateService.changeQuantity(cart?.totalItemsQuantity ?? 0)),
      )
      .subscribe();
  }

  deleteCart(): Observable<DeleteCartResponse> {
    return this.#deleteCart.mutate(undefined);
  }

  getCartJob(): Observable<CartJob | undefined> {
    return this.#getCartJob.fetch().pipe(map((response) => response.data?.cart));
  }

  getCart() {
    return this.#getCartQuery.fetch().pipe(map((response) => response.data?.cart));
  }

  addToCart(variables: AddToCartVariables): Observable<AddLineItemToCartResponse | undefined> {
    const isB2BUser = this.#userService.isB2BUser();

    if (isB2BUser && !variables.jobKey) {
      throw new Error('jobKey is required for B2B users');
    }

    return (
      isB2BUser
        ? this.#addToCartMutation.mutate(variables, { fetchPolicy: 'network-only', errorPolicy: 'all' }).pipe(
            map((response) => {
              if (response.errors) {
                throw new ApolloError({ graphQLErrors: response.errors });
              }
              return response.data?.addLineItemToCart;
            }),
          )
        : this.#addToMyCartMutation.mutate(variables, { fetchPolicy: 'network-only', errorPolicy: 'all' }).pipe(
            map((response) => {
              if (response.errors) {
                throw new ApolloError({ graphQLErrors: response.errors });
              }

              return response.data?.addLineItemToMyCart;
            }),
          )
    ).pipe(tap(() => this.#cartStateService.addQuantity(variables.quantity)));
  }
}
